import Head from 'next/head';

import { IOC } from '@/ioc/index';
import { defaultLocale, locales, type LanguageMap } from '@/src/shared/utils/i18n';
import { getFullPageDescription, getFullPageTitle, siteName } from '@/src/shared/utils/metadata';

export interface PageMetadataProps {
  hrefLang: LanguageMap;
  title?: string;
  withTitlePrefix?: boolean;
  description?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogImageUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImageUrl?: string;
  twitterImageAlt?: string;
  robots?: string;
  forceNoIndex?: boolean;
}

export function PageMetadata({
  title,
  description,
  hrefLang,
  canonicalUrl,
  robots,
  forceNoIndex,
  ogTitle,
  ogDescription,
  ogUrl = '',
  ogImageUrl,
  twitterTitle,
  twitterDescription,
  twitterImageUrl,
  twitterImageAlt
}: PageMetadataProps) {
  const metasUrl = IOC.get('ClientEnvVars').clientUrl;

  ogImageUrl ||= `${metasUrl}/assets/images/pepeog.webp`;
  twitterImageUrl ||= `${metasUrl}/assets/images/pepeog.webp`;

  return (
    <Head>
      <title>{getFullPageTitle(title)}</title>
      <meta name="description" content={getFullPageDescription(description)} />

      {robots && <meta name="robots" content={robots} />}
      {!robots && forceNoIndex && (
        <>
          <meta name="robots" content="noindex, nofollow, nocache" />
          <meta name="googlebot" content="noindex, nofollow, noimageindex" />
        </>
      )}

      {/* openGraph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={getFullPageTitle(ogTitle)} />
      <meta property="og:description" content={getFullPageDescription(ogDescription)} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:image:url" content={ogImageUrl} />

      {/* twitter */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={getFullPageTitle(twitterTitle)} />
      <meta name="twitter:description" content={getFullPageDescription(twitterDescription)} />
      <meta name="twitter:image" content={twitterImageUrl} />
      <meta name="twitter:image:alt" content={twitterImageAlt} />

      {/* alternates */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {locales.map((language) => {
        const href = hrefLang[language];

        return href ? (
          <link key={language} rel="alternate" hrefLang={language} href={href} />
        ) : null;
      })}
      <link rel="alternate" hrefLang="x-default" href={hrefLang?.[defaultLocale] || ''} />

      {/* icons */}
      {/* // TODO */}

      {/* viewport */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
    </Head>
  );
}
